import { ProjectElementImport } from '@shared/models/element-import.model';
import { getTimeSinceEvent, toDateTime } from '@shared/helpers/date.helpers';

const formatImportCreatedAtParts = (
  createdAt: string,
): { date: string; time: string } => {
  const d = new Date(createdAt);
  return {
    date: d
      .toLocaleDateString('sv-SE', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
      .replace(/\./g, ''),
    time: d.toLocaleTimeString('sv-SE', {
      hour: '2-digit',
      minute: '2-digit',
    }),
  };
};

/** Imported-at label for side-panel metadata: `<date> – <time>`. */
export const formatImportCreatedAt = (imp: ProjectElementImport): string => {
  const { date, time } = formatImportCreatedAtParts(imp.createdAt);
  return `${date} – ${time}`;
};

/**
 * Relative created-at label for import file rows, e.g. `5 min ago`, `22 jun`.
 * Uses {@link getTimeSinceEvent} thresholds (minutes → hours → days → date).
 */
export const formatImportRowCreatedAt = (createdAt: string): string => {
  const relative = getTimeSinceEvent(toDateTime(createdAt));
  if (relative.endsWith(' ago') || relative.startsWith('in ')) return relative;
  return relative.toLowerCase();
};

export type ImportLabelParts = {
  fileName: string;
  /** Relative timestamp shown on the import row. */
  dateTime: string;
  /** Full absolute label for tooltips and search. */
  fullLabel: string;
};

/** Split import row label into a truncatable file name and a fixed date/time suffix. */
export const getImportLabelParts = (
  imp: ProjectElementImport,
): ImportLabelParts => {
  const fileName = imp.fileName ?? 'Import';
  const dateTime = formatImportRowCreatedAt(imp.createdAt);
  const fullLabel = `${fileName} · ${formatImportCreatedAt(imp)}`;
  return {
    fileName,
    dateTime,
    fullLabel,
  };
};

/**
 * Full absolute import row label: `<fileName> · <created date> – <time>`.
 * Used as the group-row `name` fallback and tooltip text.
 */
export const formatImportLabel = (imp: ProjectElementImport): string =>
  getImportLabelParts(imp).fullLabel;

/** Progress label for import completion, e.g. `15 of 300 completed`. */
export const formatImportProgressLabel = (
  resolved: number,
  total: number,
): string => `${resolved} of ${total} completed`;
